// an object of branch and matching value
import { isObject } from 'lodash';
import {
  compose,
  when,
  always,
  reduce,
  omit,
  merge,
  find,
  pathOr,
  equals
} from 'ramda';
import { IGuarantor } from './auth.util';
interface PatternO {
  [branch: string]: any;
}

interface IGetByRelationshipAdditionalData {
  guardian_1: IGuarantor;
}

const matchValue = (value: string | number, pattern: PatternO) => {
  const cases = Object.keys(pattern);
  const lastIndex = cases.length - 1;
  const hasCorrectCase = (key: string, index: number) => {
    if (String(value) === key) return true;
    if (key === '_') {
      if (index !== lastIndex) {
        throw new Error(`'_' must be the last branch.`);
      }

      return true;
    }

    return false;
  };
  const matchingCase = cases.find(hasCorrectCase);

  if (!matchingCase) {
    throw new ReferenceError(`Match error for value: ${value}`);
  }

  return pattern[matchingCase];
};

const match = (pattern: any) => (value: any) => matchValue(value, pattern);

const setCookie = (
  name: string,
  value: string | number | object,
  options: { duration: number }
) => {
  const { duration } = options;

  const expireDate = new Date();
  expireDate.setTime(expireDate.getTime() + duration * 3600 * 1000);

  let cookieString = isObject(value)
    ? `${name}=${JSON.stringify(value)}`
    : `${name}=${value}`;

  cookieString = cookieString.concat(
    '; ',
    `expires=${expireDate.toUTCString()};path=/`
  );

  document.cookie = cookieString;
};

const deleteCookie = (cookieName) => {
  const cookieDate = new Date(); // current date & time
  // set expired time to the past so that the cookie is invalid in the present
  cookieDate.setTime(cookieDate.getTime() - 10000);
  document.cookie = `${cookieName}=; expires=${cookieDate.toUTCString()};path=/`;
};

const getCookie = (cookieName, valueIsObject = false) => {
  const results = document.cookie.match(`(^|;) ?${cookieName}=([^;]*)(;|$)`);

  if (results) {
    if (valueIsObject) {
      try {
        return JSON.parse(results[2]);
      } catch (error) {
        // handle error
        // tslint:disable-next-line: no-console
        console.log('should be parse error', error);
      }
    }

    return results[2];
  }

  return null;
};

const deleteAllCookie = () => {
  document.cookie.split(';').forEach((cookie) => {
    document.cookie = cookie
      .replace(/^ +/, '')
      .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
  });
};

const calculateTotalWidget = (dataArray, key, isToFixed = false) =>
  compose(
    when(always(isToFixed), (res: number) => res.toFixed(2)),
    reduce((acc, cur: any) => acc + cur[key], 0)
  )(dataArray);

const isKeyPressEnterEvent = (event) => {
  const charCode = event.which ? event.which : event.keyCode;

  if (charCode === 13) return true;

  return false;
};

const parseJSON = (objectKey, fallbackValue = {}) => {
  try {
    return JSON.parse(
      localStorage.getItem(objectKey) || JSON.stringify(fallbackValue)
    );
  } catch (err) {
    return fallbackValue;
  }
};

const capitalizeFirstLetter = (str) => {
  if (!str) return str;

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const EMAIL_REGEX = /^(([*!#$%&'\/\=\*\^\~\?\+-|`{}\w*]){1,64}@\w+([-.]\w+)*\.\w+([-.]\w+)*\s*[,]?\b)*$/;

interface IContactAttribute {
  name: string;
  value: string;
}

const normalizeContactAttributes = reduce(
  (result, attribute: IContactAttribute) => {
    return {
      ...result,
      [attribute.name]: JSON.parse(attribute.value) || attribute.value
    };
  },
  {}
);

const normalizeGuardian = compose(omit(['contactAttributes']), (g) => {
  const guardianContactAttributes: IContactAttribute[] = pathOr(
    [],
    ['contactAttributes']
  )(g);

  return merge(normalizeContactAttributes(guardianContactAttributes))(g);
});

const getRelationship = (
  type: string,
  additionalData?: IGetByRelationshipAdditionalData
) => {
  const guardian_1: IGuarantor | undefined = pathOr(undefined, ['guardian_1'])(
    additionalData
  );

  const findSecondGuardianIfMoreThanOneGuardianExists = (guarantor) =>
    guarantor.familyAccess &&
    guarantor.relationship === 'Guardian' &&
    guardian_1 &&
    !equals(guarantor.id, (guardian_1 as IGuarantor).id);

  const findSecondGuardianIfNoMoreThanOneGuardianExists = (guarantor) =>
    guarantor.familyAccess &&
    ['Mother', 'Father'].includes(guarantor.relationship);

  const matchers = {
    spouse: (guarantor) =>
      guarantor.familyAccess && guarantor.relationship === 'Spouse',
    guardian_1: (guarantor) =>
      guarantor.familyAccess && guarantor.relationship === 'Guardian',
    guardian_2: guardian_1
      ? findSecondGuardianIfMoreThanOneGuardianExists
      : findSecondGuardianIfNoMoreThanOneGuardianExists
  };

  if (['spouse', 'guardian_1', 'guardian_2'].includes(type)) {
    const matcher = matchers[type];

    return compose(normalizeGuardian, find(matcher));
  }

  if (type === 'billing') {
    return compose(normalizeGuardian, pathOr({}, [0]));
  }

  return (g) => g;
};

const shallowEqual = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
  for (const key in a) {
    if (!b.hasOwnProperty(key) || a[key] !== b[key]) {
      return false;
    }
  }

  for (const key in b) {
    if (!a.hasOwnProperty(key)) return false;
  }

  return true;
}

const transformUserInfoPhoneNumber = (userInfo) => {
  const typeOrder = {
    mobile: 1,
    home: 2,
    work: 3,
  };

  const originalPhoneNumbers = userInfo.phoneNumbers;

  const sortedPhoneNumbers = originalPhoneNumbers.sort((a, b) => {
    const typeA = a.type.toLowerCase();
    const typeB = b.type.toLowerCase();

    if (typeOrder[typeA] && typeOrder[typeB]) {
      return typeOrder[typeA] - typeOrder[typeB];
    }

    if (!typeOrder[typeA]) return 1;
    if (!typeOrder[typeB]) return -1;

    return 0;
  });

  const sortedPhoneNumberWithExtension = sortedPhoneNumbers.map((phoneNumber)=>{
    const phoneWithExtension = `${phoneNumber.number}${phoneNumber.extension ? `-${phoneNumber.extension}` : ''}`;

    return {...phoneNumber, number: phoneWithExtension }
  });

  return { ...userInfo, phoneNumbers: sortedPhoneNumberWithExtension };
};

export {
  match,
  setCookie,
  deleteCookie,
  getCookie,
  deleteAllCookie,
  calculateTotalWidget,
  isKeyPressEnterEvent,
  parseJSON,
  EMAIL_REGEX,
  capitalizeFirstLetter,
  normalizeContactAttributes,
  getRelationship,
  shallowEqual,
  transformUserInfoPhoneNumber
};
